"use client";

import React, { useState, useEffect } from 'react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { EmailConfig, DEFAULT_EMAIL_CONFIG } from '@/types/emailConfig';
import { emailConfigService } from '@/lib/api/emailConfigService';

const TEMPLATE_VARS = [
  { key: '{clienteNombre}', desc: 'Nombre completo del cliente' },
  { key: '{empresa}', desc: 'Empresa del cliente' },
  { key: '{monto}', desc: 'Monto solicitado' },
  { key: '{plazo}', desc: 'Plazo en meses' },
  { key: '{asesor}', desc: 'Nombre del asesor' },
  { key: '{numeroControl}', desc: 'Número de control' },
  { key: '{nombreJefe}', desc: 'Nombre del jefe inmediato' },
  { key: '{correoJefe}', desc: 'Correo del jefe inmediato' },
];

type Section = 'smtp' | 'destinatarios' | 'plantilla';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

const inputCls =
  'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500 bg-white';
const labelCls = 'block text-sm font-medium text-gray-700 mb-1';
const sectionBtnCls = (active: boolean) =>
  `px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
    active
      ? 'bg-teal-600 text-white'
      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
  }`;

export const EmailConfigModal: React.FC<Props> = ({ isOpen, onClose }) => {
  const [section, setSection] = useState<Section>('smtp');
  const [config, setConfig] = useState<EmailConfig>({ ...DEFAULT_EMAIL_CONFIG });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [msg, setMsg] = useState<{ type: 'ok' | 'error'; text: string } | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);
    setMsg(null);
    emailConfigService.getConfig().then((res) => {
      if (res.data) setConfig({ ...res.data, smtp_password: '' });
      setLoading(false);
    });
  }, [isOpen]);

  const set = (field: keyof EmailConfig, value: any) =>
    setConfig((prev) => ({ ...prev, [field]: value }));

  const handleSave = async () => {
    setSaving(true);
    setMsg(null);
    // Si smtp_password está vacío, no enviar para no sobreescribir
    const payload: EmailConfig = {
      ...config,
      smtp_password: config.smtp_password || undefined,
    };
    const res = await emailConfigService.saveConfig(payload);
    if (res.data?.ok) {
      setMsg({ type: 'ok', text: 'Configuración guardada correctamente.' });
    } else {
      setMsg({ type: 'error', text: res.error || 'Error al guardar.' });
    }
    setSaving(false);
  };

  const handleTest = async () => {
    if (!testEmail.trim()) return;
    setTesting(true);
    setMsg(null);
    const res = await emailConfigService.testSend(testEmail.trim());
    if (res.data?.ok) {
      setMsg({ type: 'ok', text: res.data.message || 'Correo enviado correctamente.' });
    } else {
      setMsg({ type: 'error', text: res.error || 'Error al enviar correo de prueba.' });
    }
    setTesting(false);
  };

  const insertVar = (field: 'subject_template' | 'body_template', v: string) =>
    set(field, (config[field] || '') + v);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Configuración de Correo — VoBo"
      footer={
        <div className="flex justify-between items-center gap-3 pt-4 border-t border-gray-200">
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cerrar
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={saving || loading}>
            {saving ? 'Guardando...' : 'Guardar Configuración'}
          </Button>
        </div>
      }
    >
      <div className="space-y-5">
        {/* Mensaje de estado */}
        {msg && (
          <div
            className={`p-3 rounded-lg border text-sm font-medium ${
              msg.type === 'ok'
                ? 'bg-green-50 border-green-200 text-green-700'
                : 'bg-red-50 border-red-200 text-red-700'
            }`}
          >
            {msg.text}
          </div>
        )}

        {loading && (
          <div className="text-center py-6 text-gray-500 text-sm">Cargando configuración…</div>
        )}

        {!loading && (
          <>
            {/* Navegación de secciones */}
            <div className="flex gap-2 flex-wrap">
              <button className={sectionBtnCls(section === 'smtp')} onClick={() => setSection('smtp')}>
                Servidor SMTP
              </button>
              <button className={sectionBtnCls(section === 'destinatarios')} onClick={() => setSection('destinatarios')}>
                Destinatarios
              </button>
              <button className={sectionBtnCls(section === 'plantilla')} onClick={() => setSection('plantilla')}>
                Plantilla del Correo
              </button>
            </div>

            {/* ── SMTP ── */}
            {section === 'smtp' && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls}>Servidor SMTP *</label>
                    <input
                      className={inputCls}
                      placeholder="smtp.gmail.com"
                      value={config.smtp_host}
                      onChange={(e) => set('smtp_host', e.target.value)}
                    />
                  </div>
                  <div>
                    <label className={labelCls}>Puerto *</label>
                    <input
                      className={inputCls}
                      type="number"
                      placeholder="587"
                      value={config.smtp_port}
                      onChange={(e) => set('smtp_port', Number(e.target.value))}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls}>Usuario SMTP</label>
                    <input
                      className={inputCls}
                      placeholder="correo@empresa.com"
                      value={config.smtp_user}
                      onChange={(e) => set('smtp_user', e.target.value)}
                    />
                  </div>
                  <div>
                    <label className={labelCls}>
                      Contraseña SMTP
                      {config.smtp_password_set && (
                        <span className="ml-2 text-xs text-teal-600 font-normal">
                          (ya configurada — dejar vacío para no cambiar)
                        </span>
                      )}
                    </label>
                    <input
                      className={inputCls}
                      type="password"
                      placeholder={config.smtp_password_set ? '••••••••' : 'Contraseña'}
                      value={config.smtp_password || ''}
                      onChange={(e) => set('smtp_password', e.target.value)}
                      autoComplete="new-password"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls}>Correo remitente *</label>
                    <input
                      className={inputCls}
                      placeholder="noreply@caabsa.com"
                      value={config.from_email}
                      onChange={(e) => set('from_email', e.target.value)}
                    />
                  </div>
                  <div>
                    <label className={labelCls}>Nombre remitente</label>
                    <input
                      className={inputCls}
                      placeholder="CAABSA"
                      value={config.from_name}
                      onChange={(e) => set('from_name', e.target.value)}
                    />
                  </div>
                </div>

                <div className="flex gap-6">
                  <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700">
                    <input
                      type="checkbox"
                      className="rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                      checked={config.use_tls}
                      onChange={(e) => set('use_tls', e.target.checked)}
                    />
                    Usar TLS (STARTTLS)
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700">
                    <input
                      type="checkbox"
                      className="rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                      checked={config.use_ssl}
                      onChange={(e) => set('use_ssl', e.target.checked)}
                    />
                    Usar SSL (puerto 465)
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700">
                    <input
                      type="checkbox"
                      className="rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                      checked={config.is_active}
                      onChange={(e) => set('is_active', e.target.checked)}
                    />
                    Envío activo
                  </label>
                </div>

                {/* Prueba de envío */}
                <div className="border-t border-gray-200 pt-4">
                  <p className="text-sm font-medium text-gray-700 mb-2">Prueba de envío</p>
                  <div className="flex gap-2">
                    <input
                      className={inputCls}
                      type="email"
                      placeholder="correo@destino.com"
                      value={testEmail}
                      onChange={(e) => setTestEmail(e.target.value)}
                    />
                    <Button
                      variant="secondary"
                      onClick={handleTest}
                      disabled={testing || !testEmail.trim()}
                    >
                      {testing ? 'Enviando…' : 'Enviar prueba'}
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* ── DESTINATARIOS ── */}
            {section === 'destinatarios' && (
              <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-700">
                  Define quién recibirá el correo cuando se envíen VoBos. Se puede combinar
                  destinatarios automáticos con correos fijos.
                </div>

                <div className="space-y-3">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      className="mt-0.5 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                      checked={config.send_to_jefe}
                      onChange={(e) => set('send_to_jefe', e.target.checked)}
                    />
                    <div>
                      <p className="text-sm font-medium text-gray-800">
                        Jefe inmediato del cliente
                      </p>
                      <p className="text-xs text-gray-500">
                        Usa el campo "Correo de Jefe Inmediato" de cada solicitud
                      </p>
                    </div>
                  </label>

                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      className="mt-0.5 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                      checked={config.send_to_cliente}
                      onChange={(e) => set('send_to_cliente', e.target.checked)}
                    />
                    <div>
                      <p className="text-sm font-medium text-gray-800">Cliente</p>
                      <p className="text-xs text-gray-500">
                        Usa el campo "Correo del Cliente" de cada solicitud
                      </p>
                    </div>
                  </label>
                </div>

                <div>
                  <label className={labelCls}>
                    Correos adicionales (TO)
                    <span className="ml-1 text-xs text-gray-400 font-normal">
                      separados por coma
                    </span>
                  </label>
                  <input
                    className={inputCls}
                    placeholder="gerencia@empresa.com, otro@empresa.com"
                    value={config.extra_recipients}
                    onChange={(e) => set('extra_recipients', e.target.value)}
                  />
                </div>

                <div>
                  <label className={labelCls}>
                    Correos en copia (CC)
                    <span className="ml-1 text-xs text-gray-400 font-normal">
                      separados por coma
                    </span>
                  </label>
                  <input
                    className={inputCls}
                    placeholder="archivo@empresa.com, supervisor@empresa.com"
                    value={config.cc_emails}
                    onChange={(e) => set('cc_emails', e.target.value)}
                  />
                </div>
              </div>
            )}

            {/* ── PLANTILLA ── */}
            {section === 'plantilla' && (
              <div className="space-y-4">
                {/* Variables disponibles */}
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                    Variables disponibles (clic para insertar en el cuerpo)
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {TEMPLATE_VARS.map(({ key, desc }) => (
                      <button
                        key={key}
                        title={desc}
                        onClick={() => insertVar('body_template', key)}
                        className="px-2 py-0.5 bg-teal-100 text-teal-800 rounded text-xs font-mono hover:bg-teal-200 transition-colors"
                      >
                        {key}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className={labelCls}>Asunto *</label>
                  <div className="flex gap-2 items-start">
                    <input
                      className={inputCls}
                      placeholder="Solicitud de VoBo — {empresa} — {clienteNombre}"
                      value={config.subject_template}
                      onChange={(e) => set('subject_template', e.target.value)}
                    />
                    <div className="relative group">
                      <button
                        className="px-2 py-2 text-xs bg-gray-100 rounded-lg hover:bg-gray-200"
                        title="Insertar variable en asunto"
                      >
                        + Var
                      </button>
                      <div className="hidden group-hover:block absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 w-48 p-1">
                        {TEMPLATE_VARS.map(({ key, desc }) => (
                          <button
                            key={key}
                            onClick={() => insertVar('subject_template', key)}
                            className="w-full text-left px-2 py-1 text-xs hover:bg-gray-50 rounded"
                          >
                            <span className="font-mono text-teal-700">{key}</span>
                            <span className="text-gray-400 ml-1">— {desc}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <label className={labelCls}>Cuerpo del correo *</label>
                  <textarea
                    className={`${inputCls} resize-y`}
                    rows={12}
                    placeholder="Escriba aquí el cuerpo del correo. Use las variables de arriba."
                    value={config.body_template}
                    onChange={(e) => set('body_template', e.target.value)}
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    El correo se envía como texto plano. Los saltos de línea se respetan.
                  </p>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  );
};
